import React from 'react'
import { cn } from '@/lib/utils'
import { Moon, Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  TOGGLE_THUMB,
  TOGGLE_THUMB_OFF,
  TOGGLE_THUMB_ON,
  TOGGLE_TRACK,
  TOGGLE_TRACK_OFF,
  TOGGLE_TRACK_ON,
} from './controlStyles'

function trackClassName(checked: boolean, disabled: boolean) {
  return cn(
    TOGGLE_TRACK,
    checked ? TOGGLE_TRACK_ON : TOGGLE_TRACK_OFF,
    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
  )
}

interface ToggleSwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label?: string
  description?: string
  icon?: React.ReactNode
  className?: string
  disabled?: boolean
  tooltip?: string
  /**
   * Accessible name for the toggle button when no visible ``label`` is shown.
   * Also used as hover text, so an unlabelled toggle explains itself like an icon button.
   */
  ariaLabel?: string
}

export function ToggleSwitch({
  checked,
  onChange,
  label,
  description,
  icon,
  className,
  disabled = false,
  tooltip,
  ariaLabel
}: ToggleSwitchProps) {
  return (
    <div className={cn('flex items-center justify-between', className)}>
      <div className="flex items-center gap-3">
        {icon && (
          <div
            className="text-[var(--text-secondary)]"
            title={tooltip}
          >
            {icon}
          </div>
        )}
        <div className={cn(!label && 'flex items-center gap-3')}>
          {label && (
            <label className="block text-sm font-medium text-[var(--text-color)]">
              {label}
            </label>
          )}
          {description && (
            <p className={cn(
              "text-sm text-[var(--text-secondary)]",
              !label && "text-xs"
            )}>
              {description}
            </p>
          )}
        </div>
      </div>

      <button
        type="button"
        role="switch"
        data-slot="toggle-switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        title={ariaLabel}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (!disabled) {
            onChange(!checked)
          }
        }}
        disabled={disabled}
        className={trackClassName(checked, disabled)}
      >
        <span className={cn(TOGGLE_THUMB, checked ? TOGGLE_THUMB_ON : TOGGLE_THUMB_OFF)} />
      </button>
    </div>
  )
}

// Spezielle Dark Mode Toggle-Komponente
interface DarkModeToggleProps {
  checked: boolean
  onChange: (checked: boolean) => void
  className?: string
  disabled?: boolean
}

export function DarkModeToggle({
  checked,
  onChange,
  className,
  disabled = false
}: DarkModeToggleProps) {
  const { t } = useTranslation()
  return (
    <div className={cn('flex items-center justify-between', className)}>
      <div className="flex items-center gap-3">
        <div className="text-[var(--text-secondary)]">
          {checked ? <Moon size={20} /> : <Sun size={20} />}
        </div>
      </div>
      
      <button
        type="button"
        data-slot="toggle-switch"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (!disabled) {
            onChange(!checked)
          }
        }}
        disabled={disabled}
        className={trackClassName(checked, disabled)}
        aria-label={checked ? t('common.switchToLightMode') : t('common.switchToDarkMode')}
      >
        <span className={cn(TOGGLE_THUMB, checked ? TOGGLE_THUMB_ON : TOGGLE_THUMB_OFF)} />
      </button>
    </div>
  )
}
